import Text from 'UIKit/Text'
import { Game } from 'config/types/game'
import useDebounceCallback from 'hooks/useDebounceCallback'
import { useRequest } from 'hooks/useRequest'
import { RowCenter } from 'layout/Components/Row'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import GameService from 'services/GameService'
import { useWagerAdventure } from 'state/app/hooks'
import { useActiveBonusInWageringProcess } from 'state/bonus/hooks'
import { useFeaturedGame } from 'state/profile/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import { useIsWagerAdventure } from 'views/WagerAdventure/hook'

const LowWagerMultiplierAwareBadge: React.FC<{ game: Game }> = ({ game }) => {
  const { execute } = useRequest()
  const { t } = useTranslation()
  const bonus = useActiveBonusInWageringProcess()
  const [wagerMultiplier, setWagerMultiplier] = useState<number>(1)
  const [wagerAdventureMultiplier, setWagerAdventureMultiplier] = useState<number>(1)

  const {
    featuredGameInfo: { isWageringBonusGame },
  } = useFeaturedGame(game.code)
  const wagerAdventure = useWagerAdventure()
  const isGameWagerAdventure = useIsWagerAdventure(
    wagerAdventure?.endDate < Date.now() ? null : wagerAdventure?.gameCollectionId,
    game,
  )

  const debounceCallback = useDebounceCallback()

  const fetch = async () => {
    const bonusGameId = bonus && isWageringBonusGame ? bonus?.gameCollectionId : null
    const adventureGameId = wagerAdventure && isGameWagerAdventure ? wagerAdventure?.gameCollectionId : null

    if (!bonusGameId) setWagerMultiplier(1)
    if (!adventureGameId) setWagerAdventureMultiplier(1)

    if (bonusGameId) {
      const bonusWagerMultiplier = await execute(
        GameService.getGameWagerMultiplier({ gameCollectionId: bonusGameId, gameCode: game.code }),
      )
      setWagerMultiplier(bonusWagerMultiplier?.data || 1)
    }

    if (adventureGameId) {
      const adventureMultiplier = await execute(
        GameService.getGameWagerMultiplier({ gameCollectionId: adventureGameId, gameCode: game.code }),
      )
      setWagerAdventureMultiplier(adventureMultiplier?.data || 1)
    }
  }

  useEffect(() => {
    debounceCallback(fetch, 500)
  }, [bonus && isWageringBonusGame, game, isGameWagerAdventure])

  return (
    (wagerMultiplier < 1 || wagerAdventureMultiplier < 1) && (
      <StyledContainer
        background={colors.strokeAlt3}
        width="100%"
        height={['40px', '', '', '48px']}
        px="20px"
        py="8px"
        mx="auto"
        borderRadius="4px"
      >
        <StyledWarningIcon width="24px" height="24px" fill={colors.textTertiary} />
        <Text
          fontSize={['12px', '', '', '14px']}
          fontWeight={400}
          lineHeight="14px"
          letterSpacing="-0.28px"
          color="textTertiary"
        >
          {wagerMultiplier !== wagerAdventureMultiplier && wagerMultiplier < 1 && wagerAdventureMultiplier < 1 ? (
            t('This game contributes {{ amount  }}% bonus wagering, {{ amountAdventrure }}% wager adventure.', {
              amount: wagerMultiplier,
              amountAdventrure: wagerAdventureMultiplier,
            })
          ) : wagerMultiplier < 1 ? (
            <Trans>This game contributes {{ amount: wagerMultiplier }}% bonus wagering.</Trans>
          ) : (
            wagerAdventureMultiplier < 1 && (
              <Trans>This game contributes {{ amount: wagerAdventureMultiplier }}% bonus wagering.</Trans>
            )
          )}
        </Text>
      </StyledContainer>
    )
  )
}

const StyledContainer = styled(RowCenter)`
  @keyframes hide {
    0% {
      height: auto;
    }
    80% {
      height: 0px;
      opacity: 0;
      padding: 0px;
      overflow: hidden;
    }

    100% {
      height: 0px;
      opacity: 0;
      padding: 0px;
      overflow: hidden;
      display: none;
    }
  }

  animation: hide 0.35s linear forwards;
  animation-delay: 3s;
`

const StyledWarningIcon = styled(Icons.WarningIconCircle)`
  transform: scale(0.8);
  width: 30px;
  display: flex;
  justify-content: center;
  margin-right: 2px;
`

export default LowWagerMultiplierAwareBadge
